export interface VerificationResult {
  readonly userId: string;
}

type VerificationErrorEnvelope = {
  readonly error?: {
    readonly message?: unknown;
  };
};

function apiBaseUrl(): string {
  return (import.meta.env.VITE_CUSTOMER_API_BASE_URL ?? '').replace(/\/$/, '');
}

async function post<T>(path: string, body: unknown, fallbackMessage: string): Promise<T> {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const payload: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const envelope = payload as VerificationErrorEnvelope | undefined;
    const message =
      typeof envelope?.error?.message === 'string' ? envelope.error.message : fallbackMessage;
    throw new Error(message);
  }
  return payload as T;
}

export async function verifyCustomerEmail(
  email: string,
  code: string,
): Promise<VerificationResult> {
  const payload = await post<{ userId: string }>(
    '/api/v1/auth/verify-email',
    { email, code },
    'We could not verify your email right now. Please try again.',
  );
  return { userId: payload.userId };
}

export async function resendCustomerVerificationCode(email: string): Promise<void> {
  await post(
    '/api/v1/auth/verify-email/resend',
    { email },
    'We could not send another verification code right now. Please try again.',
  );
}

export async function requestCustomerPasswordReset(email: string): Promise<void> {
  await post(
    '/api/v1/auth/password-reset',
    { email },
    'We could not request a password reset right now. Please try again.',
  );
}

export async function confirmCustomerPasswordReset(token: string, password: string): Promise<void> {
  await post(
    '/api/v1/auth/password-reset/confirm',
    { token, password },
    'We could not reset your password right now. Please request a new link and try again.',
  );
}

export async function acceptCustomerInvitation(
  token: string,
  password: string,
  fullName?: string,
): Promise<VerificationResult> {
  const payload = await post<{ userId: string }>(
    '/api/v1/auth/accept-invitation',
    { token, password, ...(fullName === undefined ? {} : { fullName }) },
    'This invitation is invalid, expired, or has already been used.',
  );
  return { userId: payload.userId };
}
